/* =========================================================================
   ClueVine — Supporter entitlement (account-lite monetization)
   A local boolean: did this person support ClueVine? Unlocked when they return
   from the Stripe Payment Link with the ?supporter flag (honor system, no
   backend). Kept in its own slice so it survives a settings reset. The support
   and tip links come from the admin-managed site config.
   ========================================================================= */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { SUPPORTER_REDIRECT_FLAG } from '../config';
import { useToast } from '../components/Toast';
import { loadSlice, saveSlice, type SliceConfig } from './persistence';
import { SKIN_LOCKED, useSettings } from './SettingsContext';
import { useSiteConfig } from './SiteConfigContext';

const slice: SliceConfig<boolean> = {
  name: 'supporter',
  version: 1,
  defaults: () => false,
};

interface SupporterContextValue {
  isSupporter: boolean;
  /** Whether a Stripe link is configured (controls whether to show the button). */
  canSupport: boolean;
  /** Whether a tip-jar link is configured. */
  canTip: boolean;
  grant: () => void;
  revoke: () => void;
  openSupport: () => void;
  openTip: () => void;
}

const SupporterContext = createContext<SupporterContextValue | null>(null);

export function SupporterProvider({ children }: { children: ReactNode }) {
  const { config } = useSiteConfig();
  const { settings, update: updateSettings } = useSettings();
  const toast = useToast();
  const [isSupporter, setIsSupporter] = useState<boolean>(() => loadSlice(slice) === true);

  // Honor-system unlock: Stripe "after payment" redirect lands on /?supporter=…
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has(SUPPORTER_REDIRECT_FLAG)) {
        setIsSupporter(true);
        saveSlice(slice, true);
        toast.show('Thank you for supporting ClueVine! 🌱 Bonus skins are unlocked in Settings.', 'success');
        params.delete(SUPPORTER_REDIRECT_FLAG);
        const query = params.toString();
        window.history.replaceState(
          {},
          '',
          window.location.pathname + (query ? `?${query}` : '') + window.location.hash,
        );
      }
    } catch {
      /* no-op */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    saveSlice(slice, isSupporter);
  }, [isSupporter]);

  // Bonus skins are a supporter perk: a revoked unlock (or an imported backup)
  // mustn't leave one applied.
  useEffect(() => {
    if (!isSupporter && SKIN_LOCKED[settings.skin]) updateSettings({ skin: 'default' });
  }, [isSupporter, settings.skin, updateSettings]);

  const grant = useCallback(() => setIsSupporter(true), []);
  const revoke = useCallback(() => setIsSupporter(false), []);
  const openSupport = useCallback(() => {
    if (config.supportUrl) window.open(config.supportUrl, '_blank', 'noopener,noreferrer');
  }, [config.supportUrl]);
  const openTip = useCallback(() => {
    if (config.tipUrl) window.open(config.tipUrl, '_blank', 'noopener,noreferrer');
  }, [config.tipUrl]);

  const value = useMemo<SupporterContextValue>(
    () => ({
      isSupporter,
      canSupport: config.supportUrl.length > 0,
      canTip: config.tipUrl.length > 0,
      grant,
      revoke,
      openSupport,
      openTip,
    }),
    [isSupporter, config.supportUrl, config.tipUrl, grant, revoke, openSupport, openTip],
  );

  return <SupporterContext.Provider value={value}>{children}</SupporterContext.Provider>;
}

export function useSupporter(): SupporterContextValue {
  const ctx = useContext(SupporterContext);
  if (!ctx) throw new Error('useSupporter must be used within <SupporterProvider>');
  return ctx;
}
